import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const FAQItem = ({ q, a, index, open, onToggle }) => (
  <div className="border-b border-stone" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      data-testid={`faq-question-${index}`}
      aria-expanded={open}
      className="w-full flex justify-between items-center gap-6 py-6 text-left group"
    >
      <span className="font-serif text-xl md:text-2xl text-ink group-hover:text-bronze transition-colors">{q}</span>
      <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.25 }} className="shrink-0 text-bronze">
        <Plus size={22} className="text-azul" aria-hidden />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-7 pr-10 text-ink/75 leading-relaxed max-w-3xl" data-testid={`faq-answer-${index}`}>{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const { t, lang } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div data-testid="faq-page">
      <Seo
        title={lang === "es" ? "Preguntas frecuentes — NIE Valencia, empadronamiento y más | Marina Knows" : "FAQ — NIE Valencia, Empadronamiento & More | Marina Knows"}
        description={lang === "es"
          ? "Respuestas claras: cuánto tarda el NIE en Valencia, si necesitas empadronamiento para el banco, diferencia entre NIE y TIE, citas previas y más."
          : "Straight answers: how long a NIE takes in Valencia, whether you need empadronamiento before a bank account, NIE vs TIE, cita previa help and more."}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">{t.faqPage.overline}</p>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-ink mb-6" data-testid="faq-title">{t.faqPage.title}</h1>
            <p className="text-lg text-ink/70 mb-8">{t.faqPage.sub}</p>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-cta"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-6 py-3 rounded-full transition-all hover:-translate-y-0.5"
            >
              <MessageCircle size={16} aria-hidden />
              {t.faqPage.cta}
            </a>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <div className="border-t border-stone">
                {t.faqPage.items.map((item, i) => (
                  <FAQItem
                    key={i}
                    index={i}
                    q={item.q}
                    a={item.a}
                    open={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                  />
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
